import React, { useEffect, useState } from 'react'
import { Card, Button, Statistic, Table, Space, Typography, Tag, Input, Tooltip, Modal } from 'antd'
import { 
  ShareAltOutlined, 
  CopyOutlined, 
  UserAddOutlined, 
  GiftOutlined,
  CheckCircleOutlined,
  QrcodeOutlined,
  TeamOutlined,
  TrophyOutlined,
  FireOutlined,
  WechatOutlined,
  QqOutlined
} from '@ant-design/icons'
import { App } from 'antd'
import { apiClient } from '@/services/api'
import { motion } from 'framer-motion'
import { COMMON_STYLES } from '@/styles/globalStyles'
import '@/styles/global.css'

const { Title, Paragraph, Text } = Typography

interface ReferralStats {
  code: string
  shareUrl: string
  totalReferrals: number
  totalRewards: number
  referrals: Array<{
    id: string
    referee: {
      id: string
      username: string
      email?: string
      createdAt: string
    }
    reward: number
    status: string
    createdAt: string
    completedAt?: string
  }>
}

const ReferralPage: React.FC = () => {
  const { message: messageApi } = App.useApp()
  const [loading, setLoading] = useState(false)
  const [stats, setStats] = useState<ReferralStats | null>(null)
  const [copied, setCopied] = useState(false)
  const [qrCodeVisible, setQrCodeVisible] = useState(false)

  // 加载推荐统计
  const loadStats = async () => {
    try {
      setLoading(true)
      const response = await apiClient.get('/referral/stats')
      
      if (response.success && response.data) {
        setStats(response.data)
      } else {
        messageApi.error('加载推荐统计失败：响应格式错误')
      }
    } catch (error: any) {
      console.error('[ReferralPage] 加载推荐统计失败:', error)
      messageApi.error(error.response?.data?.message || '加载推荐统计失败')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadStats()
  }, [])

  // 复制推荐码
  const handleCopyCode = () => {
    if (stats?.code) {
      navigator.clipboard.writeText(stats.code)
      setCopied(true)
      messageApi.success('推荐码已复制到剪贴板')
      setTimeout(() => setCopied(false), 2000)
    }
  }

  // 复制分享链接
  const handleCopyLink = () => {
    if (stats?.shareUrl) {
      const fullUrl = stats.shareUrl.startsWith('http') 
        ? stats.shareUrl 
        : `${window.location.origin}/${stats.shareUrl.replace(/^\//, '')}`
      navigator.clipboard.writeText(fullUrl)
      messageApi.success('分享链接已复制到剪贴板')
    }
  }

  // 获取完整分享链接
  const getFullShareUrl = () => {
    if (!stats?.shareUrl) return ''
    return stats.shareUrl.startsWith('http') 
      ? stats.shareUrl 
      : `${window.location.origin}/${stats.shareUrl.replace(/^\//, '')}`
  }

  // 分享到社交媒体
  const handleShare = async () => {
    const fullUrl = getFullShareUrl()
    if (!fullUrl) {
      messageApi.warning('分享链接未准备好，请稍后再试')
      return
    }

    const shareText = `邀请您加入 AI 创作平台！使用我的推荐码注册可获得30积分奖励，我也能获得50积分奖励！`

    // 优先使用 Web Share API（移动端支持）
    if (navigator.share && navigator.canShare && navigator.canShare({ url: fullUrl })) {
      try {
        await navigator.share({
          title: '邀请您加入 AI 创作平台',
          text: shareText,
          url: fullUrl
        })
        messageApi.success('分享成功')
      } catch (error: any) {
        // 用户取消分享不显示错误
        if (error.name !== 'AbortError') {
          console.error('分享失败:', error)
          // 分享失败时回退到复制链接
          handleCopyLink()
        }
      }
    } else {
      // 桌面浏览器或不支持 Web Share API 时，显示分享选项
      handleCopyLink()
      messageApi.info('链接已复制，您可以粘贴到微信、QQ等应用分享')
    }
  }

  // 显示二维码
  const handleShowQRCode = () => {
    const fullUrl = getFullShareUrl()
    if (!fullUrl) {
      messageApi.warning('分享链接未准备好，请稍后再试')
      return
    }
    setQrCodeVisible(true)
  }

  // 生成二维码URL
  const getQRCodeUrl = () => {
    const fullUrl = getFullShareUrl()
    if (!fullUrl) return ''
    return `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(fullUrl)}`
  }

  // 分享到微信
  const handleShareWechat = () => {
    const fullUrl = getFullShareUrl()
    if (!fullUrl) return
    // 微信分享需要特殊处理，这里先复制链接
    handleCopyLink()
    messageApi.info('链接已复制，请打开微信粘贴分享')
  }

  // 分享到QQ
  const handleShareQQ = () => {
    const fullUrl = getFullShareUrl()
    if (!fullUrl) return
    const shareText = encodeURIComponent('邀请您加入 AI 创作平台！使用我的推荐码注册可获得30积分奖励！')
    const qqUrl = `https://connect.qq.com/widget/shareqq/index.html?url=${encodeURIComponent(fullUrl)}&title=${shareText}`
    window.open(qqUrl, '_blank', 'width=600,height=400')
  }

  // 表格列定义
  const columns = [
    {
      title: '被推荐人',
      dataIndex: ['referee', 'username'],
      key: 'username',
      render: (text: string, record: any) => (
        <div>
          <Text strong>{text}</Text>
          {record.referee.email && (
            <div>
              <Text type="secondary" style={{ fontSize: '12px' }}>{record.referee.email}</Text>
            </div>
          )}
        </div>
      )
    },
    {
      title: '注册时间',
      dataIndex: ['referee', 'createdAt'],
      key: 'createdAt',
      render: (date: string) => new Date(date).toLocaleString('zh-CN')
    },
    {
      title: '奖励积分',
      dataIndex: 'reward',
      key: 'reward',
      render: (reward: number) => (
        <Tag color="green" icon={<GiftOutlined />}>
          +{reward}
        </Tag>
      )
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        status === 'COMPLETED' ? (
          <Tag color="success" icon={<CheckCircleOutlined />}>已完成</Tag>
        ) : (
          <Tag color="default">待完成</Tag>
        )
      )
    }
  ]

  return (
    <div className="min-h-screen w-full relative">
      {/* 内容区域 - 应用统一样式 */}
      <div className="unified-container relative z-10" style={{
        paddingTop: 'var(--spacing-sm)'
      }}>
        <motion.div
          {...COMMON_STYLES.pageEnter}
          transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
          className="flex flex-col"
        >
          {/* 头部设计 - 左右结构 */}
          <div className="w-full page-header-section">
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.05, ease: [0.4, 0, 0.2, 1] }}
              className="flex flex-col md:flex-row md:items-center md:justify-between"
              style={{
                marginBottom: 'var(--spacing-md)',
                gap: 'var(--spacing-sm)'
              }}
            >
              {/* 左侧：标题和副标题 */}
              <div className="flex-1">
                <div className="flex items-center" style={{
                  gap: 'var(--spacing-sm)',
                  marginBottom: 'var(--spacing-xs)'
                }}>
                  <span style={{ fontSize: 'var(--font-h1-mobile)' }}>🎁</span>
                  <h1 className="unified-title-h1 mb-0 bg-gradient-to-r from-pink-600 via-purple-600 to-blue-600 bg-clip-text text-transparent">
                    推荐好友
                  </h1>
                </div>
                <Paragraph className="unified-text-secondary" style={{
                  fontSize: '13px',
                  marginLeft: 'var(--spacing-xl)',
                  marginBottom: 0
                }}>
                  邀请好友注册，双方都能获得积分奖励
                </Paragraph>
              </div>
              
              {/* 右侧：特色标签 */}
              <div className="flex flex-wrap md:justify-end" style={{ gap: 'var(--spacing-sm)' }}>
                <Tag icon={<GiftOutlined />} className="unified-tag bg-gradient-to-r from-green-50 to-green-100 text-green-700">
                  双倍奖励
                </Tag>
                <Tag icon={<TrophyOutlined />} className="unified-tag bg-gradient-to-r from-yellow-50 to-yellow-100 text-yellow-700">
                  永久有效
                </Tag>
                <Tag icon={<FireOutlined />} className="unified-tag bg-gradient-to-r from-red-50 to-red-100 text-red-700">
                  立即到账
                </Tag>
              </div>
            </motion.div>
          </div>

          {/* 主内容区 */}
          <div className="w-full">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6 mb-4 md:mb-6 items-stretch">
              {/* 左侧：推荐码卡片 - 占2列 */}
              <div className="lg:col-span-2">
                <motion.div 
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: 0.1, ease: [0.4, 0, 0.2, 1] }}
                  className="bg-white shadow-lg border border-gray-100 unified-card h-full flex flex-col"
                  style={{
                    borderRadius: 'var(--radius-xl)',
                    padding: 'var(--card-padding)'
                  }}
                >
                  {/* 移动端顶部装饰 */}
                  <div className="md:hidden bg-gradient-to-r from-pink-500 via-purple-500 to-blue-500 h-1.5 w-full rounded-full mb-3"></div>

                  <div className="flex items-center gap-2 mb-3" style={{ marginBottom: 'var(--spacing-md)' }}>
                    <span className="text-lg">🎫</span>
                    <Text className="unified-text-primary font-bold" style={{ fontSize: '16px' }}>我的推荐码</Text>
                    <div className="flex-1"></div>
                    <Tooltip title="复制推荐码">
                      <Button 
                        type="text" 
                        size="small"
                        icon={copied ? <CheckCircleOutlined /> : <CopyOutlined />}
                        onClick={handleCopyCode}
                        className="flex items-center justify-center"
                      />
                    </Tooltip>
                  </div>

                  {stats?.code ? (
                    <div className="text-center flex-1 flex flex-col justify-center" style={{ 
                      paddingTop: 'var(--spacing-md)',
                      paddingBottom: 'var(--spacing-md)'
                    }}>
                      <motion.div
                        initial={{ scale: 0.95, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ duration: 0.3, delay: 0.15, ease: [0.4, 0, 0.2, 1] }}
                        className="text-4xl md:text-5xl font-bold mb-4 tracking-wider"
                        style={{
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          backgroundClip: 'text',
                          marginBottom: 'var(--spacing-md)'
                        }}
                      >
                        {stats.code}
                      </motion.div>
                      <Paragraph type="secondary" className="text-sm mb-0" style={{ 
                        fontSize: '13px',
                        marginBottom: 0
                      }}>
                        分享此推荐码给好友，双方都能获得积分奖励
                      </Paragraph>
                    </div>
                  ) : (
                    <div className="text-center flex-1 flex items-center justify-center" style={{ 
                      paddingTop: 'var(--spacing-lg)',
                      paddingBottom: 'var(--spacing-lg)'
                    }}>
                      <Text type="secondary">加载中...</Text>
                    </div>
                  )}
                </motion.div>
              </div>

              {/* 右侧：统计卡片 - 占1列，使用 flex 布局使高度一致 */}
              <div className="lg:col-span-1 flex flex-col gap-4 md:gap-6 h-full">
                {/* 推荐人数统计 */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.3 }}
                  className="flex-1"
                >
                  <Card 
                    className="unified-card bg-white shadow-lg border border-gray-100 h-full flex flex-col justify-center" 
                    style={{
                      borderRadius: 'var(--radius-xl)'
                    }}
                    styles={{
                      body: {
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        padding: 'var(--card-padding)'
                      }
                    }}
                  >
                    <Statistic
                      title={
                        <div className="flex items-center gap-2" style={{ marginBottom: 'var(--spacing-sm)' }}>
                          <UserAddOutlined style={{ color: '#3b82f6', fontSize: '16px' }} />
                          <span style={{ fontSize: '14px', fontWeight: 600 }}>推荐人数</span>
                        </div>
                      }
                      value={stats?.totalReferrals || 0}
                      valueStyle={{ 
                        color: '#3b82f6', 
                        fontSize: '32px', 
                        fontWeight: 'bold',
                        marginTop: 'var(--spacing-xs)',
                        lineHeight: 1.2
                      }}
                      prefix={<TeamOutlined style={{ fontSize: '28px', marginRight: '6px' }} />}
                    />
                  </Card>
                </motion.div>

                {/* 获得奖励统计 */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                  className="flex-1"
                >
                  <Card 
                    className="unified-card bg-white shadow-lg border border-gray-100 h-full flex flex-col justify-center" 
                    style={{
                      borderRadius: 'var(--radius-xl)'
                    }}
                    styles={{
                      body: {
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        padding: 'var(--card-padding)'
                      }
                    }}
                  >
                    <Statistic
                      title={
                        <div className="flex items-center gap-2" style={{ marginBottom: 'var(--spacing-sm)' }}>
                          <GiftOutlined style={{ color: '#10b981', fontSize: '16px' }} />
                          <span style={{ fontSize: '14px', fontWeight: 600 }}>获得奖励</span>
                        </div>
                      }
                      value={stats?.totalRewards || 0}
                      suffix={<span style={{ fontSize: '16px', marginLeft: '4px' }}>积分</span>}
                      valueStyle={{ 
                        color: '#10b981', 
                        fontSize: '32px', 
                        fontWeight: 'bold',
                        marginTop: 'var(--spacing-xs)',
                        lineHeight: 1.2
                      }}
                      prefix={<TrophyOutlined style={{ fontSize: '28px', marginRight: '6px' }} />}
                    />
                  </Card>
                </motion.div>
              </div>
            </div>

            {/* 分享区域 */}
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.2, ease: [0.4, 0, 0.2, 1] }}
            >
              <div className="bg-white shadow-lg border border-gray-100 unified-card mb-4 md:mb-6" style={{
                borderRadius: 'var(--radius-xl)',
                padding: 'var(--card-padding)'
              }}>
                {/* 移动端顶部装饰 */}
                <div className="md:hidden bg-gradient-to-r from-pink-500 via-purple-500 to-blue-500 h-1.5 w-full rounded-full mb-3"></div>

                <div className="flex items-center gap-2 mb-3" style={{ marginBottom: 'var(--spacing-md)' }}>
                  <span className="text-lg">🔗</span>
                  <Text className="unified-text-primary font-bold" style={{ fontSize: '16px' }}>分享推荐</Text>
                </div>

                <Space direction="vertical" size="middle" className="w-full">
                  <div>
                    <Text strong className="mb-2 block" style={{ fontSize: '14px', marginBottom: 'var(--spacing-sm)' }}>分享链接：</Text>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Input
                        value={stats?.shareUrl ? (stats.shareUrl.startsWith('http') ? stats.shareUrl : `${window.location.origin}/${stats.shareUrl.replace(/^\//, '')}`) : ''}
                        readOnly
                        className="flex-1 min-w-[200px]"
                        size="small"
                        style={{
                          borderRadius: 'var(--radius-md)'
                        }}
                      />
                      <Button.Group>
                        <Button 
                          icon={<CopyOutlined />} 
                          onClick={handleCopyLink}
                          size="small"
                        >
                          复制
                        </Button>
                        <Button 
                          icon={<QrcodeOutlined />}
                          onClick={handleShowQRCode}
                          size="small"
                        >
                          二维码
                        </Button>
                      </Button.Group>
                    </div>
                  </div>

                  <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-3 md:p-4 rounded-xl border border-blue-100">
                    <Paragraph className="mb-2" style={{ marginBottom: 'var(--spacing-sm)' }}>
                      <Text strong style={{ fontSize: '14px' }}>推荐奖励规则：</Text>
                    </Paragraph>
                    <ul className="mt-2 mb-0 pl-5 space-y-1.5" style={{ 
                      marginTop: 'var(--spacing-sm)',
                      marginBottom: 0,
                      paddingLeft: 'var(--spacing-xl)'
                    }}>
                      <li className="flex items-start" style={{ marginBottom: 'var(--spacing-xs)' }}>
                        <span className="mr-2" style={{ marginRight: 'var(--spacing-sm)' }}>✅</span>
                        <Text style={{ fontSize: '13px' }}>好友通过您的推荐码注册，您可获得 <Text strong style={{ color: '#10b981' }}>50 积分</Text></Text>
                      </li>
                      <li className="flex items-start" style={{ marginBottom: 'var(--spacing-xs)' }}>
                        <span className="mr-2" style={{ marginRight: 'var(--spacing-sm)' }}>✅</span>
                        <Text style={{ fontSize: '13px' }}>好友注册时也可获得 <Text strong style={{ color: '#3b82f6' }}>30 积分</Text> 奖励</Text>
                      </li>
                      <li className="flex items-start">
                        <span className="mr-2" style={{ marginRight: 'var(--spacing-sm)' }}>✅</span>
                        <Text style={{ fontSize: '13px' }}>奖励在好友注册成功后立即发放</Text>
                      </li>
                    </ul>
                  </div>

                  {/* 快速分享按钮 */}
                  <div>
                    <Text strong className="mb-2 block" style={{ fontSize: '14px', marginBottom: 'var(--spacing-sm)' }}>快速分享：</Text>
                    <Space size="small" wrap>
                      <Button 
                        icon={<WechatOutlined />}
                        onClick={handleShareWechat}
                        size="small"
                        style={{
                          borderRadius: 'var(--radius-md)',
                          color: '#07c160',
                          borderColor: '#07c160'
                        }}
                      >
                        微信
                      </Button>
                      <Button 
                        icon={<QqOutlined />}
                        onClick={handleShareQQ}
                        size="small"
                        style={{
                          borderRadius: 'var(--radius-md)',
                          color: '#12b7f5',
                          borderColor: '#12b7f5'
                        }}
                      >
                        QQ
                      </Button>
                    </Space>
                  </div>
                </Space>
              </div>
            </motion.div>

            {/* 二维码Modal */}
            <Modal
              title={
                <div className="flex items-center gap-2">
                  <QrcodeOutlined />
                  <span>扫描二维码分享</span>
                </div>
              }
              open={qrCodeVisible}
              onCancel={() => setQrCodeVisible(false)}
              footer={[
                <Button key="copy" icon={<CopyOutlined />} onClick={() => {
                  handleCopyLink()
                  setQrCodeVisible(false)
                }}>
                  复制链接
                </Button>,
                <Button key="close" onClick={() => setQrCodeVisible(false)}>
                  关闭
                </Button>
              ]}
              centered
              width={400}
            >
              <div className="flex flex-col items-center justify-center py-4">
                <img 
                  src={getQRCodeUrl()} 
                  alt="分享二维码" 
                  style={{ 
                    width: '300px', 
                    height: '300px',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    padding: '10px',
                    backgroundColor: '#fff'
                  }}
                />
                <Paragraph className="text-center mt-4 mb-0" style={{ 
                  marginTop: 'var(--spacing-md)',
                  marginBottom: 0,
                  fontSize: '13px',
                  color: '#666'
                }}>
                  使用微信、QQ等应用扫描二维码即可分享
                </Paragraph>
              </div>
            </Modal>

            {/* 推荐记录表格 */}
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.25, ease: [0.4, 0, 0.2, 1] }}
            >
              <div className="bg-white shadow-lg border border-gray-100 unified-card" style={{
                borderRadius: 'var(--radius-xl)',
                padding: 'var(--card-padding)'
              }}>
                {/* 移动端顶部装饰 */}
                <div className="md:hidden bg-gradient-to-r from-pink-500 via-purple-500 to-blue-500 h-1.5 w-full rounded-full mb-3"></div>

                <div className="flex items-center gap-2 mb-3" style={{ marginBottom: 'var(--spacing-md)' }}>
                  <span className="text-lg">📊</span>
                  <Text className="unified-text-primary font-bold" style={{ fontSize: '16px' }}>推荐记录</Text>
                </div>

                {loading ? (
                  <div className="text-center py-8" style={{ paddingTop: 'var(--spacing-lg)', paddingBottom: 'var(--spacing-lg)' }}>
                    <Text type="secondary">加载中...</Text>
                  </div>
                ) : stats?.referrals && stats.referrals.length > 0 ? (
                  <Table
                    columns={columns}
                    dataSource={stats.referrals}
                    rowKey="id"
                    size="small"
                    pagination={{
                      pageSize: 10,
                      showSizeChanger: false,
                      showTotal: (total) => `共 ${total} 条记录`,
                      style: { marginTop: 'var(--spacing-md)' }
                    }}
                  />
                ) : (
                  <div className="text-center py-8" style={{ paddingTop: 'var(--spacing-lg)', paddingBottom: 'var(--spacing-lg)' }}>
                    <div className="mb-3" style={{ marginBottom: 'var(--spacing-md)' }}>
                      <UserAddOutlined style={{ fontSize: '40px', color: '#d1d5db' }} />
                    </div>
                    <Text type="secondary" style={{ fontSize: '14px' }}>
                      暂无推荐记录，快去邀请好友吧！
                    </Text>
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </div>
  )
}

export default ReferralPage
